/**
 * WHOSE SCHEDULING CHAIN IS THIS TICKET IN?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * FLOYD-8. A ticket sat in the Parking Lot, Active and virgin, with its Start
 * Time two minutes in the past. The Dayboard drew it correctly — "Parked —
 * scheduled for Joe D" — and General AutoBump never moved it, because
 * AutoBump could not see it.
 *
 * `tickets_parking_lot_immunity` forbids `parking_lot = true` while
 * `assigned_tech_id` is set. Parking a scheduled ticket therefore CLEARS the
 * active assignment and leaves `scheduled_tech_id` carrying the plan. Three
 * places had to know that and only two did:
 *
 *   TechnicianColumn   knew        (its own inline two-shape rule)
 *   localCascade       knew        (its own .or() clause)
 *   useAutoBump        did NOT     (`if (!ticket.assigned_tech_id) return;`)
 *   globalCascade      did NOT     (`.not('assigned_tech_id','is',null)`)
 *
 * Four copies of one rule, two of them wrong. So the rule lives here once and
 * the four ask it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE RULE
 * ═══════════════════════════════════════════════════════════════════════════
 *   (A) `assigned_tech_id` — active, in-bay ownership. Always wins.
 *   (B) otherwise, for a PARKED ticket whose immunity cleared the active
 *       assignment, `scheduled_tech_id` — the surviving dispatch plan.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS DELIBERATELY DOES NOT DECIDE
 * ═══════════════════════════════════════════════════════════════════════════
 * Ownership is orthogonal to LIFECYCLE. This answers "whose chain", never
 * "should it move". Held-in-Parking (parked + Paused), terminal states and
 * devirginized tickets are excluded by the status and virginity filters that
 * already exist at each call site — putting them here would quietly turn a
 * General AutoBump repair into Pause AutoBump behaviour, and would give the
 * cascades a second opinion about lifecycle they never asked for.
 *
 * A parked ticket with NO `scheduled_tech_id` is Parking-Lot-inactive: it
 * belongs to no technician's chain, and returning null for it is the whole
 * reason (B) is conditioned on the field being present.
 *
 * Mirrored for Deno in `supabase/functions/_shared/chainOwnership.ts`; the two
 * are asserted to agree by `tests/unit/server/chainOwnershipParity.test.ts`.
 */

export interface ChainOwnableTicket {
  assigned_tech_id?: string | null;
  scheduled_tech_id?: string | null;
  parking_lot?: boolean | null;
  /** OCT28 #13: a parked repair whose capacity was released keeps its technician only as the plan. */
  schedule_state_reason?: string | null;
}

/** OCT28 #13 — server reasons for a parked repair whose unused capacity was released (waiting on an outside
 *  dependency, or resolved and waiting for a fresh placement). It is in NO technician's chain until it is placed. */
export const RELEASED_HOLD_REASONS: ReadonlySet<string> = new Set(['awaiting_external_resolution', 'interrupted_ready_for_placement']);

/**
 * OCT28 #13 — may `myTechId` operate this ticket's timer from the Parking Lot? Only for their own released repair once the
 * front office has marked the dependency resolved. A released repair is in no technician's column (rule (B) below), so
 * without this the planned technician had no Resume anywhere. The resume itself is still the server's call: it pulls the
 * car into a bay through the placement guard. Before "ready" nothing is offered (the server would refuse it anyway).
 */
export function mayResumeReadyInterruptedRepair(
  t: (ChainOwnableTicket & { status?: string | null }) | null | undefined,
  myTechId: string | null | undefined,
): boolean {
  return !!t && !!myTechId && t.parking_lot === true && t.status !== 'Completed'
    && t.schedule_state_reason === 'interrupted_ready_for_placement' && t.scheduled_tech_id === myTechId;
}

/**
 * The technician whose scheduling chain this ticket belongs to, or null.
 *
 * Null means "no technician's chain" — not "unknown". A caller that treats
 * null as a technician is the defect this module exists to end.
 */
export function effectiveChainOwnerId(t: ChainOwnableTicket | null | undefined): string | null {
  if (!t) return null;

  // (A) An active assignment always wins. A ticket being worked belongs to
  // whoever is working it, whatever the plan once said.
  const assigned = t.assigned_tech_id;
  if (assigned != null && String(assigned).length > 0) return String(assigned);

  // (B) Parked, with the assignment cleared by immunity. The plan is what is
  // left, and it is what the Dayboard is already drawing.
  // OCT28 #13: unless its capacity was released — then the plan is only a preference, not a reservation.
  if (t.parking_lot === true && !RELEASED_HOLD_REASONS.has(String(t.schedule_state_reason ?? ''))) {
    const scheduled = t.scheduled_tech_id;
    if (scheduled != null && String(scheduled).length > 0) return String(scheduled);
  }

  return null;
}

/** Is this ticket in `techId`'s scheduling chain? */
export function ownsInChain(
  t: ChainOwnableTicket | null | undefined,
  techId: string | null | undefined,
): boolean {
  if (!techId) return false;
  return effectiveChainOwnerId(t) === techId;
}

/**
 * Is this ticket in SOME technician's chain?
 *
 * The shop-wide form, for callers that bucket rather than filter to one
 * technician.
 */
export function hasChainOwner(t: ChainOwnableTicket | null | undefined): boolean {
  return effectiveChainOwnerId(t) !== null;
}
